// Dates and numbers in the language the reader chose.
//
// WHY THIS EXISTS
// P01 found an English checkout announcing "Next charge on 29 września 2026" — a
// Polish month name inside an English sentence, on a message about money. The
// cause was `toLocaleDateString('pl-PL', …)` written once and never revisited.
//
// Measured across this tree on 2026-08-29: 188 non-test source files, 12
// locale-sensitive formatting calls, and ALL TWELVE hardcode 'pl-PL'. Not one
// passes the active locale. So this is not one screen's slip — every date and
// every number an English-reading customer sees is formatted in Polish, and the
// only reason nobody noticed is that we all work in Polish.
//
// The fix has to be a shared function for the same reason `pluralForm` is one:
// twelve call sites re-deriving "which BCP-47 tag does 'en' mean" will disagree,
// and the ones that disagree quietly are the ones that ship.
//
// EN maps to en-GB, not en-US: day-month-year keeps the same field order as
// Polish, so a layout drawn against one does not reflow in the other.
//
// THE ALL-NUMERIC FORM IS NOT FOR MONEY OR DEADLINES.
// The first version of this note argued that en-GB is safe because "29/09 is
// never read as September". CRITIC took that apart, and they were right — it is
// half inverted. 29 is safe precisely BECAUSE there is no month 29. The
// dangerous dates are the ones where BOTH parts are <= 12:
//
//     05/09/2026   a British reader: 5 September   ·   an American: 9 May
//                  four months apart, with nothing on screen saying so
//
// So `{ day: '2-digit', month: '2-digit' }` must not be used for a charge date,
// an invoice date or a publication deadline — the surfaces where being four
// months out is a payment dispute rather than a localisation bug. Use the
// DEFAULT below, which carries a month NAME and reads the same in every variety
// of English; where something compact is genuinely needed, `formatDateISO` is
// unambiguous everywhere. The en-GB choice keeps applying to lists and tables,
// where the field-order argument above is the whole of it.
//
// This is not left to a comment. `__tests__/locale-formatters.test.ts` pins the
// default to a month NAME using a date whose parts are both <= 12, so a later
// tidy-up to digits turns red instead of shipping.

/** The two locales the app ships. Kept local (not imported from lib/i18n) so
 *  this module has no dependency on the provider — same reason as plural.ts. */
export type FormatLocale = 'en' | 'pl'

const BCP47: Record<FormatLocale, string> = { pl: 'pl-PL', en: 'en-GB' }

/** The BCP-47 tag for a UI locale. Use this rather than writing a tag inline. */
export function intlLocale(locale: FormatLocale): string {
  return BCP47[locale] ?? BCP47.en
}

/** An ISO string as a Date, or null when it is missing or unparseable. */
function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * A date in the reader's language.
 *
 * `fallback` is what to render for a missing or broken value — screens differ
 * ('—', '-', ''), and that choice belongs to the screen, not here.
 */
export function formatDate(
  locale: FormatLocale,
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
  fallback = '-',
): string {
  const d = parse(iso)
  return d ? d.toLocaleDateString(intlLocale(locale), opts) : fallback
}

/**
 * A calendar-day KEY (`YYYY-MM-DD`) in the reader's language — the same day everywhere.
 *
 * A bare key is a day of the calendar, not an instant: `new Date('2026-09-02')` is UTC
 * midnight, so a reader west of UTC (New York, UTC-4) saw it as 1 September on the usage
 * chart and the Stats axis (PROD2 F2-1). Read at LOCAL NOON instead, the form every sibling
 * that labels a day key already uses (`DaySheet`, `DayStrip`, `MonthCalendar`,
 * `StatsPostDialog`): no zone on Earth is twelve hours away from its own noon. Anything
 * that is not a bare key goes through `formatDate` unchanged.
 */
export function formatDayKey(
  locale: FormatLocale,
  key: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
  fallback = '-',
): string {
  return formatDate(locale, key && /^\d{4}-\d{2}-\d{2}$/.test(key) ? `${key}T12:00:00` : key, opts, fallback)
}

/** A date AND a time in the reader's language. */
export function formatDateTime(
  locale: FormatLocale,
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
  fallback = '-',
): string {
  const d = parse(iso)
  return d ? d.toLocaleString(intlLocale(locale), opts) : fallback
}

/** A time of day in the reader's language. */
export function formatTime(
  locale: FormatLocale,
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
  fallback = '',
): string {
  const d = parse(iso)
  return d ? d.toLocaleTimeString(intlLocale(locale), opts) : fallback
}

/**
 * A compact date that cannot be misread: `2026-09-29`, identical in every locale.
 *
 * It exists so that "I need something short here" never has to end in
 * 2-digit/2-digit. Deliberately NOT localised — that is the whole point.
 */
export function formatDateISO(iso: string | null | undefined, fallback = '-'): string {
  const d = parse(iso)
  if (!d) return fallback
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * The thousands separator of this product, everywhere: U+202F NARROW NO-BREAK SPACE.
 *
 * WHY IT IS DEFINED IN ONE PLACE (paczka P1 B, 21.09)
 * The owner read "6  762" and "35  550" in Statystyki on his phone - a hole a whole digit
 * wide. The census this package ran found THREE independent groupings in the tree, each
 * with a different character: `lib/money.grouped` (U+202F), this formatter through Intl
 * (U+00A0, what Polish ICU hands back) and `routes/Pricing.count` (an ordinary, BREAKABLE
 * space). Three characters meant three different gaps on screens that sit next to each
 * other, and the one the owner was looking at was the widest of them.
 *
 * WHY THIS CHARACTER, MEASURED RATHER THAN CHOSEN
 * The defect is not really in the character: a fixed-width face gives every space a digit's
 * cell, however narrow the space is meant to be. Measured in the browser on our own woff2
 * files (`scripts/p1-shots.mjs`, results in `EVIDENCE/.../miary-*.json`), in em:
 *
 *                       U+00A0   U+202F   U+2009   digit
 *     JetBrains Mono     0.600    0.300    0.200    0.600
 *     DM Sans            0.256    0.128    0.200    0.690
 *
 * U+202F is the widest of these that is still clearly not a digit, and it is non-breaking
 * (line-break class GL), so a number cannot split across it. On the reported screen it takes
 * the gap from 0.600 em to 0.300 em - from exactly one digit to half of one.
 *
 * TWO NARROWER ROUTES WERE TRIED AND REJECTED, both with the measurement that killed them:
 *   - pinning the width at the FACE (an @font-face limited to the separator by
 *     `unicode-range`) does not work at all: Blink draws SPACE characters from the run's
 *     primary font and ignores the range, which CSS.getPlatformFontsForNode confirmed by
 *     naming JetBrains-Mono-Medium for the separator glyph every time;
 *   - U+2009 THIN SPACE is 0.200 em in EITHER face, but it breaks (class BA), so it needs
 *     U+2060 WORD JOINERs around it. That reaches 0.200 em - and puts two invisible format
 *     characters inside every number in the product, which survive whitespace normalisation:
 *     find-in-page for "6 762" stops working, and so does every `getByText('3 514')`.
 *     Not worth 0.1 em.
 *
 * Negative `word-spacing` on the numeric classes would also reach the target, and is the
 * route to take if half a digit is judged too wide - but it is about 140 'JetBrains Mono'
 * declarations in 40 stylesheets, which is a list the next screen will miss.
 *
 * The character is invisible in a diff; `__tests__/thousands-separator.test.ts` is the only
 * thing that would notice a "tidy-up" back to an ordinary space.
 */
export const GROUP_SEP = '\u202f'

/**
 * A whole count grouped in the reader's language: "3 240" (U+202F) in Polish, "3,240" in
 * English - the same marks `formatNumber` puts between the same digits, so a token amount and
 * a Statystyki figure on adjacent screens do not group two ways (paczka N11, P1-05).
 *
 * Hand-rolled rather than through Intl, for the reason `lib/money.amount` is: Polish ICU does
 * not group four-digit numbers by default and older runtimes disagree on the character, and a
 * balance must read the same on every phone.
 */
export function groupCount(locale: FormatLocale, n: number): string {
  const sep = locale === 'en' ? ',' : GROUP_SEP
  const digits = String(Math.abs(Math.trunc(n)))
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += sep
    out += digits[i]
  }
  return Math.trunc(n) < 0 ? `-${out}` : out
}

/**
 * A number in the reader's language - grouping and decimal separator differ.
 *
 * The separator ICU hands back (U+00A0 for Polish) is re-cut to `GROUP_SEP`, so that the two
 * formatters this tree has - this one and `groupCount` above - put the same mark between the
 * same digits. English keeps its comma: nothing to re-cut.
 */
export function formatNumber(
  locale: FormatLocale,
  n: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '-'
  // Only a separator BETWEEN DIGITS is re-cut: a space a caller put next to a unit
  // ("4,0 MB" is assembled outside) is a word space and stays one.
  // NO LOOKBEHIND (paczka N11, P1-01): Safari before 16.4 cannot parse it and the build targets
  // safari16, so the regex would throw inside render. The digit before is captured and put
  // back instead; the lookahead after it is ES3 and consumes nothing, so "1 234 567" re-cuts
  // both gaps in one pass. `__tests__/safari16-baseline.test.ts` rejects a lookbehind anywhere.
  return n.toLocaleString(intlLocale(locale), opts).replace(/(\d)\u00a0(?=\d)/g, `$1${GROUP_SEP}`)
}
